// BUILD-046 J1a (Customer / Job association): how a task's job is shown, and when the Compact "Customer / Job" column exists.
//
// WHERE IT SHOWS. Comfortable: the job name is the FIRST part of the one quiet second line under the title (beside the requester badge), so it reads
// "Alvarez - Pool build - By Julie - Oct 18". Compact: the job is NEVER drawn inline after the title; it has its OWN aligned "Customer / Job" column right after
// Task (before the optional Requested column). The server gives the CURRENT name when the job directory answered (Owner decision 3), the last known name when
// it could not be asked, and a flag when the job is archived or no longer known; this module words those states, nothing more.
//
// A row the viewer sees only in its minimal (redacted) form carries no job and shows none. The label is shown to anyone who may see the task (Owner
// decision 2); it grants no access to the job itself.
//
// WHEN THE COMPACT COLUMN EXISTS. In Compact, and only when jobs are "in play" for the page: the Caller may search jobs (`GET /work/me` `canSearchJobs`), or at
// least one task the page holds carries a job (a viewer who cannot search still sees a label that is there). The page publishes that one boolean to a tiny
// store, and the table header and every row read the SAME store, so a heading and its cells can never disagree (the pattern of the "Requested" column). Against a
// server that predates jobs nothing is in play, so the page is exactly what it was.
//
// Pure module: no React, no I/O. Erasable TypeScript; type-only import.

import type { Density } from './densityStore';
import { stripTestMarker } from './displayNames';

/** The job of a task or request, validated: the stable id, the name to show, and what is known about its state. */
export interface JobShown {
  jobId: string;
  label: string;
  state?: 'active' | 'archived';
  lastKnown?: true;
  missing?: true;
}

/** A well-formed job view from the server, or null. Anything else (absent, wrong type, empty name) is no job. */
export function asJobShown(value: unknown): JobShown | null {
  if (value === null || typeof value !== 'object') return null;
  const v = value as { jobId?: unknown; label?: unknown; state?: unknown; lastKnown?: unknown; missing?: unknown };
  if (typeof v.jobId !== 'string' || v.jobId === '' || typeof v.label !== 'string') return null;
  const label = v.label.replace(/\s+/g, ' ').trim();
  if (label === '') return null;
  const out: JobShown = { jobId: v.jobId, label };
  if (v.state === 'active' || v.state === 'archived') out.state = v.state;
  if (v.lastKnown === true) out.lastKnown = true;
  if (v.missing === true) out.missing = true;
  return out;
}

/** The job a task row shows: none for a task shown in its minimal (restricted) form or one that carries none. */
export function jobOf(task: unknown): JobShown | null {
  if (task === null || typeof task !== 'object') return null;
  const t = task as { job?: unknown; redacted?: unknown };
  if (t.redacted === true) return null;
  return asJobShown(t.job);
}

/**
 * The job of one entry of `GET /work/issued`: an Everyone request carries it on the entry, any other request on its task. A task in its minimal form carries
 * none.
 */
export function jobOfIssuedEntry(raw: unknown): JobShown | null {
  if (raw === null || typeof raw !== 'object') return null;
  const entry = raw as { job?: unknown; item?: unknown };
  return asJobShown(entry.job) ?? jobOf(entry.item);
}

export interface JobLine {
  /** The name to show. */
  label: string;
  /** A short quiet word after the name when the name is not the live one: `last known name`, `not found` or `archived`; null when it is. */
  note: string | null;
  /** The hover text: the name, and why the note applies. */
  title: string;
}

/**
 * How one job reads: its name, and an honest quiet note when the name may be out of date or the job is retired. The name is SHOWN without the "(FICTIONAL)" test
 * marker (displayNames.stripTestMarker); the stored name on the job, the filter value (the job id) and what search and sort read are not changed.
 */
export function jobLineOf(job: JobShown): JobLine {
  const label = stripTestMarker(job.label);
  if (job.missing === true) {
    return { label, note: 'not found', title: `${label}. This job is no longer in the job list; this is its last known name.` };
  }
  if (job.lastKnown === true) {
    return { label, note: 'last known name', title: `${label}. The job list could not be checked, so this is the last known name.` };
  }
  if (job.state === 'archived') {
    return { label, note: 'archived', title: `${label}. This job is archived.` };
  }
  return { label, note: null, title: label };
}

/** Whether any of the lists holds a task that carries a job. */
export function anyJob(lists: readonly (readonly unknown[])[]): boolean {
  for (const list of lists) {
    if (!Array.isArray(list)) continue;
    for (const row of list) if (jobOf(row) !== null) return true;
  }
  return false;
}

/** Jobs are "in play" for the page: the Caller may search them, or a task the page holds carries one. */
export function jobsInPlay(canSearchJobs: boolean, lists: readonly (readonly unknown[])[]): boolean {
  return canSearchJobs === true || anyJob(lists);
}

/** The "Customer / Job" column exists only in Compact, and only while jobs are in play. Comfortable shows the job under the title instead. */
export function jobColumnShown(density: unknown, inPlay: boolean): boolean {
  return density === 'compact' && inPlay === true;
}

/** The job in the second line is drawn in Comfortable only; Compact has the column. */
export function jobInSecondLine(density: Density): boolean {
  return density === 'comfortable';
}

// ───────────────────────── the page's "jobs in play" flag ─────────────────────────

export interface JobsInPlayStore {
  get: () => boolean;
  set: (next: boolean) => void;
  subscribe: (listener: () => void) => () => void;
}

/** One boolean for the whole My Work page. Not stored anywhere: the page recomputes it from `GET /work/me` and the lists it holds. */
export function createJobsInPlayStore(): JobsInPlayStore {
  let value = false;
  const listeners = new Set<() => void>();
  return {
    get: () => value,
    set(next) {
      if (typeof next !== 'boolean' || next === value) return;
      value = next;
      for (const listener of [...listeners]) listener();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

/** The page's flag; the table header and the rows read it (useJobColumn.ts). */
export const jobsInPlayStore: JobsInPlayStore = createJobsInPlayStore();
